import { getFeatureFlag, isBoolean } from "@sinch/feature-flags";
import { MM_PAYMENT_TYPE } from "../../api/mm/mm.types";
import { Redirect } from "../../components/Router/Redirect";
import { config } from "../../config/client";
import {
  getAllConnectionStates,
  getAllIntegrations,
  isIntegrationActive,
} from "../../hooks/integrations";
import {
  INBOX_V2_VERSION,
  INBOX_V4_VERSION,
} from "../../hooks/useInboxConfig.constants";
import { createFakeMfeMounter } from "../../mfe/fakeMfe.ts/createFakeMfeMounter";
import type { RemoteName } from "../../mfe/mfeConfig";
import type {
  IsRouteEnabledOptions,
  RouteConfig,
  RouteConfigMenu,
  RouteConfigSubroute,
} from "../../navigationConfig/types";
import { getNewUniqueRouteKey } from "../../navigationConfig/utils/navigationConfigUtils";
import {
  ACCOUNT_SETTINGS_SSO,
  ACCOUNT_SETTINGS,
  ADDON_CARRIER_LOOKUP,
  ADDON_HLR_LOOKUP,
  ADDON_LINK_PREVIEWS,
  ADDON_MOBILE_LANDING_PAGES,
  ADDON_NUMBER_TYPE_LOOKUP,
  ADDON_SHORT_TRACKABLE_LINKS,
  ADDON,
  API_KEYS_BASIC,
  API_KEYS_HMAC,
  API_KEYS_LEGACY,
  BILLING_DETAILS,
  BROADCASTS,
  CONTACTS,
  CONTACTS_CONTACT_FIELDS_MANAGE,
  CONTACTS_SEGMENTS,
  INBOX,
  MANAGEMENT_SUB_ACCOUNTS,
  MANAGEMENT_USERS,
  MESSAGING_SEND_SCHEDULED,
  PARTNERS_LIST_CUSTOMER_ACCOUNT,
  PRODUCTS_SELECTION,
  REPORTING_ABANDONED_CART,
  REPORTING_ACTIVITY_LOG,
  REPORTING_REPORT_DELIVERY_STATUS,
  REPORTING_REPORT_DETAILED_ALL,
  REPORTING_REPORT_DETAILED_MINE,
  REPORTING_REPORT_INSIGHT_ALL,
  REPORTING_REPORT_INSIGHT_MINE,
  REPORTING_REPORT_PARTNER_CUSTOMERS,
  REPORTING_REPORT_SCHEDULED,
  REPORTING_REPORT_SUB_ACCOUNTS,
  REPORTING_REPORT_USERS,
  TEMPLATES_RESTRICTED,
  TEMPLATES,
  WEB_WIDGET,
  WORKFLOWS_ABANDONED_CART,
  WORKFLOWS,
  ADDON_DEDICATED_NUMBER,
  ADDON_FAMILIAR_SENDER,
  ADDON_ALPHA_TAG,
  SOCIAL_PREPAID_USERS,
  SOCIAL_ADMIN_ACCESS,
  SOCIAL_USERS_ACCESS,
  SOCIAL_PROMPT_ADMIN,
  SOCIAL_PROMPT_USER,
  MESSAGING_LANDING_PAGE,
  MESSAGING_LANDING_PAGE_TEMPLATE_LIST,
  WORKFLOWS_CREATE,
} from "./featureList";
import { hasAbandonedCartIntegration, hasIntegration } from "./hasIntegration";
import { getVendorChecks } from "./vendorConfig";

const isOttMfeEnabled =
  (flag: string) =>
  ({ userRights }: IsRouteEnabledOptions): boolean => {
    const isMfeFlagOn = getFeatureFlag(flag, isBoolean, false);

    if (!isMfeFlagOn || !userRights) {
      return false;
    }
    const isPrepaidUser = userRights.includes(SOCIAL_PREPAID_USERS);

    if (isPrepaidUser) {
      return getFeatureFlag("showOttMfeToPrepaidUsers", isBoolean, false);
    }

    return userRights.some((right) =>
      [
        SOCIAL_USERS_ACCESS,
        SOCIAL_ADMIN_ACCESS,
        SOCIAL_PROMPT_USER,
        SOCIAL_PROMPT_ADMIN,
      ].includes(right)
    );
  };

const MfeDashboard = {
  key: getNewUniqueRouteKey("Dashboard"),
  path: "/",
  mfe: { name: "MfeDashboard", entry: "app" },
  menu: {
    position: "top",
    icon: "mm-home",
    textKey: "menuItemDashboard",
    href: "/",
  },
} satisfies RouteConfigMenu;

const MessageMediaUpgrade = {
  key: getNewUniqueRouteKey("upgrade"),
  path: "/upgrade",
  mfe: { name: "MessageMediaUpgrade", entry: "bootstrap" },
  isEnabled: () => getFeatureFlag("enableUpgradeFromMfeCat", isBoolean, false),
} satisfies RouteConfig;

export const EcommerceRoutes = [
  "/bigcommerce/instruction",
  "/bigcommerce/dashboard",
  "/bigcommerce/redirect",
  "/shopify/redirect",
];

export const MessageMediaPublicFacingRoutes =
  config.AUTH_CONFIG.publicPaths.map(
    (route) =>
      ({
        key: getNewUniqueRouteKey(route),
        path: route,
        mfe: { name: "MessageMediaPublicFacing", entry: "bootstrap" },
      } satisfies RouteConfig)
  );

const Inbox = {
  key: getNewUniqueRouteKey("inbox"),
  path: "/messages/inbox-v2/*",
  mfe: { name: "container", entry: "app" },
  isEnabled: ({ inboxVersion }) =>
    !inboxVersion || inboxVersion === INBOX_V2_VERSION,
  allowedPermissions: [INBOX],
  menu: {
    position: "top",
    icon: "mm-messages",
    textKey: "menuItemInbox",
    href: "/messages/inbox-v2",
  },
} satisfies RouteConfigMenu;

export const InboxV2ToV4Redirect = {
  key: getNewUniqueRouteKey("inbox"),
  path: "/messages/inbox-v2/*",
  mfe: { name: "HTMXInboxRedirect" as RemoteName, entry: "MainEntry" },
  allowedPermissions: [INBOX],
  isEnabled: ({ inboxVersion }) => inboxVersion === INBOX_V4_VERSION,
  import: async () =>
    createFakeMfeMounter(<Redirect to="/messages/inbox-v4" />),
} satisfies RouteConfig;

const HTMXInbox = {
  key: getNewUniqueRouteKey("inbox"),
  path: "/messages/inbox-v4/*",
  mfe: { name: "HTMXInbox", entry: "MainEntry" },
  allowedPermissions: [INBOX],
  isEnabled: ({ inboxVersion }) => inboxVersion === INBOX_V4_VERSION,
  menu: {
    position: "top",
    icon: "mm-messages",
    textKey: "menuItemInbox",
    href: "/messages/inbox-v4",
  },
} satisfies RouteConfigMenu;

const broadcastReportSubroute = {
  key: getNewUniqueRouteKey("broadcast-reports"),
  path: "/messages/broadcasts/*",
  href: "/messages/broadcasts",
  allowedPermissions: [
    REPORTING_REPORT_DETAILED_ALL,
    REPORTING_REPORT_DETAILED_MINE,
  ],
};

const messageReportSubRouteMfe = {
  key: getNewUniqueRouteKey("sent"),
  path: "/messages/sent-messages",
  textKey: "menuItemMessagesSent",
  href: "/messages/sent-messages",
  allowedPermissions: [
    REPORTING_REPORT_DETAILED_ALL,
    REPORTING_REPORT_DETAILED_MINE,
  ],
  mfe: { name: "MessageMediaReports", entry: "" },
} satisfies RouteConfigSubroute;

const messageReportUUID = {
  key: getNewUniqueRouteKey("sent-uuid"),
  path: "/messages/sent-messages/*",
  allowedPermissions: [
    REPORTING_REPORT_DETAILED_ALL,
    REPORTING_REPORT_DETAILED_MINE,
  ],
  mfe: { name: "MessageMediaReports", entry: "" },
} satisfies RouteConfig;

export const MfeEcommerce = EcommerceRoutes.map(
  (route) =>
    ({
      key: getNewUniqueRouteKey(route),
      path: route,
      mfe: { name: "MfeEcommerce", entry: "" },
    } satisfies RouteConfig)
);

const AutomationSubRoutesContainer = {
  key: getNewUniqueRouteKey("account-automation"),
  path: "/automations",
  textKey: "menuItemAutomationMyAutomations",
  href: "/automations",
  allowedPermissions: [WORKFLOWS],
  mfe: { name: "container", entry: "app" },
  isEnabled: () => !isIntegrationActive("mailjet"),
} satisfies RouteConfigSubroute;

const AutomationSubRoutesMfe = {
  ...AutomationSubRoutesContainer,
  mfe: { name: "MfeAutomations", entry: "" },
  isEnabled: () => isIntegrationActive("mailjet"),
} satisfies RouteConfigSubroute;

const automationIdContainer = {
  key: getNewUniqueRouteKey("automation-id"),
  path: "/automations/:id",
  allowedPermissions: [WORKFLOWS],
  mfe: { name: "container", entry: "app" },
  isEnabled: () => !isIntegrationActive("mailjet"),
} satisfies RouteConfig;

const automationIdMfe = {
  ...automationIdContainer,
  mfe: { name: "MfeAutomations", entry: "" },
  isEnabled: () => isIntegrationActive("mailjet"),
} satisfies RouteConfig;

const automationTemplatesContainer = {
  key: getNewUniqueRouteKey("account-automation-templates"),
  path: "/automations/templates",
  allowedPermissions: [WORKFLOWS],
  mfe: { name: "container", entry: "" },
} satisfies RouteConfig;

const Campaigns = {
  key: getNewUniqueRouteKey("campaigns"),
  path: "/messages/new-message",
  mfe: { name: "container", entry: "app" },
  menu: {
    position: "top",
    icon: "mm-campaign",
    textKey: "menuItemMessages",
    href: "/messages/new-message",
    subRouteSections: [
      {
        textKey: "menuItemMessagesSMS",
        subRoutes: [
          {
            isEnabled: (userRights) =>
              !isOttMfeEnabled("mmCampaignsIrisMfe")(userRights),
            key: getNewUniqueRouteKey("new-message"),
            path: "/messages/new-message/*",
            textKey: "menuItemMessagesNewMessage",
            href: "/messages/new-message",
          },
          {
            isEnabled: isOttMfeEnabled("mmCampaignsIrisMfe"),
            mfe: { name: "CampaignsIris", entry: "Container" },
            key: getNewUniqueRouteKey("campaigns-iris"),
            path: "/messages/new-message/*",
            textKey: "menuItemMessagesSocialNewMessage",
            href: "/messages/new-message",
          },
          messageReportSubRouteMfe,
          {
            ...broadcastReportSubroute,
            mfe: { name: "MessageMediaReports", entry: "" },
            textKey: "menuItemMessagesBroadcastReport",
            isEnabled: () => getVendorChecks().isNZCarriers,
          },
          {
            ...broadcastReportSubroute,
            mfe: { name: "MessageMediaReports", entry: "" },
            textKey: "menuItemMessagesCampaignReport",
            isEnabled: () => !getVendorChecks().isNZCarriers,
          },
          {
            key: getNewUniqueRouteKey("scheduled-messages"),
            path: "/messages/scheduled-messages/*",
            mfe: { name: "MessageMediaReports", entry: "" },
            textKey: "menuItemMessagesScheduled",
            href: "/messages/scheduled-messages",
            allowedPermissions: [MESSAGING_SEND_SCHEDULED, BROADCASTS],
          },
          {
            key: getNewUniqueRouteKey("drafts"),
            path: "/messages/drafts/*",
            textKey: "menuItemMessagesDrafts",
            mfe: { name: "MessageMediaReports", entry: "" },
            href: "/messages/drafts",
            allowedPermissions: [BROADCASTS],
          },
          {
            key: getNewUniqueRouteKey("received-messages-mfe"),
            path: "/messages/received-messages/*",
            textKey: "menuItemMessagesReceived",
            href: "/messages/received-messages",
            allowedPermissions: [
              REPORTING_REPORT_DETAILED_ALL,
              REPORTING_REPORT_DETAILED_MINE,
              CONTACTS,
            ],
            mfe: { name: "MessageMediaReports", entry: "" },
          },
        ],
      },
      {
        textKey: "menuItemMessagesSocial",
        subRoutes: [
          {
            isEnabled: isOttMfeEnabled("mmCampaignsHermesMfe"),
            mfe: { name: "CampaignsHermes", entry: "Container" },
            key: getNewUniqueRouteKey("campaigns-social"),
            path: "/v2/campaigns/*",
            textKey: "menuItemMessagesSocialAllCampaigns",
            href: "/v2/campaigns/all",
          },
        ],
      },
    ],
  },
} satisfies RouteConfigMenu;

const Automations = {
  key: getNewUniqueRouteKey("automation"),
  path: "/integrations/abandoned-carts/*",
  mfe: { name: "container", entry: "app" },
  allowedPermissions: [WORKFLOWS_ABANDONED_CART, WORKFLOWS],
  menu: {
    position: "top",
    icon: "mm-automation",
    textKey: "menuItemAutomation",
    href: "/integrations/abandoned-carts",
    subRouteSections: [
      {
        textKey: "menuItemAutomation",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("chatbot"),
            path: "/bots/*",
            mfe: { name: "BotBuilderV2", entry: "Container" },
            textKey: "menuItemAutomationBot",
            href: "/bots",
            isEnabled: () =>
              getFeatureFlag("mmBotBuilderMfe", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("abandoned-cart-automation"),
            path: "/integrations/abandoned-carts/*",
            textKey: "menuItemAutomationCartSms",
            href: "/integrations/abandoned-carts",
            allowedPermissions: [WORKFLOWS_ABANDONED_CART],
            isEnabled: () =>
              hasAbandonedCartIntegration(
                getAllIntegrations(),
                getAllConnectionStates()
              ),
          },
          AutomationSubRoutesContainer,
          AutomationSubRoutesMfe,
          {
            key: getNewUniqueRouteKey("account-automation-templates"),
            path: "/automations/templates",
            textKey: "menuItemAutomationTemplates",
            href: "/automations/templates",
            allowedPermissions: [WORKFLOWS, WORKFLOWS_CREATE],
          },
          {
            key: getNewUniqueRouteKey("account-automation-autoresponders"),
            path: "/automations/autoresponders/*",
            textKey: "menuItemAutomationAutoresponders",
            href: "/automations/autoresponders",
            mfe: { name: "engage-autoresponders-keywords", entry: "" },
            isEnabled: () =>
              getFeatureFlag(
                "autorespondersAndKeywordsInNavMenu",
                isBoolean,
                false
              ),
          },
          {
            key: getNewUniqueRouteKey("account-automation-keywords"),
            path: "/automations/keywords/*",
            textKey: "menuItemAutomationKeywords",
            href: "/automations/keywords",
            mfe: { name: "engage-autoresponders-keywords", entry: "" },
            isEnabled: () =>
              getFeatureFlag(
                "autorespondersAndKeywordsInNavMenu",
                isBoolean,
                false
              ),
          },
        ],
      },
    ],
  },
} satisfies RouteConfigMenu;

const Templates = {
  key: getNewUniqueRouteKey("templates"),
  path: "/templates/messages/*",
  mfe: { name: "container", entry: "app" },
  allowedPermissions: [TEMPLATES, TEMPLATES_RESTRICTED],
  menu: {
    position: "top",
    icon: "mm-library",
    textKey: "menuItemTemplates",
    href: "/templates/messages",
    subRouteSections: [
      {
        textKey: "menuItemTemplates",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("templates-sms"),
            path: "/templates/messages/*",
            textKey: "menuItemTemplatesSMS",
            href: "/templates/messages",
          },
          {
            mfe: { name: "TemplateManager", entry: "Container" },
            isEnabled: ({ paymentType }) =>
              getFeatureFlag("mmTemplateManagerMfe", isBoolean, false) &&
              paymentType !== MM_PAYMENT_TYPE.Enum.PREPAID &&
              paymentType !== MM_PAYMENT_TYPE.Enum.PREPAID_MONEY,
            key: getNewUniqueRouteKey("templates-social"),
            path: "/settings/account/social-templates/*",
            textKey: "menuItemTemplatesSocialTemplates",
            href: "/settings/account/social-templates",
          },
        ],
      },
    ],
  },
} satisfies RouteConfigMenu;

const MobileLandingPages = {
  key: getNewUniqueRouteKey("mobile-landing-pages"),
  path: "/messages/landing-pages/*",
  mfe: { name: "container", entry: "app" },
  isEnabled: () => getFeatureFlag("extensionsV2", isBoolean, false),
  allowedPermissions: [
    MESSAGING_LANDING_PAGE,
    MESSAGING_LANDING_PAGE_TEMPLATE_LIST,
  ],
  menu: {
    position: "top",
    icon: "mm-sms-plus",
    textKey: "menuItemMobileLandingPages",
    href: "/messages/landing-pages",
  },
} satisfies RouteConfigMenu;

const ContactsV2 = {
  key: getNewUniqueRouteKey("contacts-v2"),
  path: "/contacts-v2/*",
  mfe: { name: "MessageMediaContactsV2", entry: "" },
  allowedPermissions: [CONTACTS],
  menu: {
    position: "top",
    icon: "mm-contact",
    textKey: "menuItemContactV2",
    href: "/contacts-v2/contacts",
    subRouteSections: [
      {
        textKey: "menuItemContactV2",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("all-contacts-v2"),
            path: "/contacts-v2/contacts/*",
            textKey: "menuItemContactV2AllContacts",
            href: "/contacts-v2/contacts",
            allowedPermissions: [CONTACTS],
          },
          {
            key: getNewUniqueRouteKey("contact-groups-v2"),
            path: "/contacts-v2/groups/*",
            textKey: "menuItemContactV2Lists",
            href: "/contacts-v2/groups",
            allowedPermissions: [CONTACTS],
          },
          {
            key: getNewUniqueRouteKey("contact-fields-v2"),
            path: "/contacts-v2/contact-fields/*",
            textKey: "menuItemContactV2ContactFields",
            href: "/contacts-v2/contact-fields",
            allowedPermissions: [CONTACTS_CONTACT_FIELDS_MANAGE],
          },
          {
            key: getNewUniqueRouteKey("all-segments-v2"),
            path: "/contacts-v2/segments/*",
            textKey: "menuItemContactV2Segments",
            href: "/contacts-v2/segments",
            allowedPermissions: [CONTACTS_SEGMENTS],
          },
          {
            key: getNewUniqueRouteKey("history-v2"),
            path: "/contacts-v2/history/*",
            textKey: "menuItemContactV2History",
            href: "/contacts-v2/history",
            allowedPermissions: [CONTACTS],
          },
        ],
      },
    ],
  },
} satisfies RouteConfigMenu;

const Extensions = {
  key: getNewUniqueRouteKey("extensions"),
  path: "/integrations",
  mfe: { name: "container", entry: "app" },
  allowedPermissions: [ADDON],
  menu: {
    position: "bottom",
    icon: "mm-extension",
    textKey: "menuItemExtensions",
    href: "/integrations",
    subRouteSections: [
      {
        textKey: "menuItemExtensions",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("integrations"),
            path: "/integrations",
            textKey: "menuItemExtensionsIntegrations",
            href: "/integrations",
            isEnabled: () => getFeatureFlag("mmIntegrations", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("extensions"),
            path: "/extensions",
            mfe: { name: "MessageMediaExtensions", entry: "app" },
            textKey: "menuItemExtensionsPowerUps",
            href: "/extensions",
            isEnabled: ({ userRights }) => {
              const hasChannelV2 = getFeatureFlag(
                "numbersAndChannelsV2",
                isBoolean,
                false
              );

              const ChannelV2PowerUps = [
                WEB_WIDGET,
                ADDON_LINK_PREVIEWS,
                ADDON_MOBILE_LANDING_PAGES,
                ADDON_SHORT_TRACKABLE_LINKS,
                ADDON_CARRIER_LOOKUP,
                ADDON_NUMBER_TYPE_LOOKUP,
                ADDON_HLR_LOOKUP,
              ];
              const ChannelPowerUps = [
                ADDON_DEDICATED_NUMBER,
                ADDON_FAMILIAR_SENDER,
                ADDON_ALPHA_TAG,
                ADDON_LINK_PREVIEWS,
                ADDON_MOBILE_LANDING_PAGES,
                ADDON_SHORT_TRACKABLE_LINKS,
                ADDON_CARRIER_LOOKUP,
                ADDON_NUMBER_TYPE_LOOKUP,
                ADDON_HLR_LOOKUP,
              ];
              const showPowerUps = hasChannelV2
                ? ChannelV2PowerUps.some((powerUp) =>
                    userRights?.includes(powerUp)
                  )
                : ChannelPowerUps.some((powerUp) =>
                    userRights?.includes(powerUp)
                  ) || false;
              return showPowerUps;
            },
          },
        ],
      },
    ],
  },
} satisfies RouteConfigMenu;

export const IntegrationRoutes = ["/integrations/*"];
export const MfeIntegrations = IntegrationRoutes.map(
  (route) =>
    ({
      key: getNewUniqueRouteKey(route),
      path: route,
      mfe: { name: "MfeIntegrations", entry: "" },
      isEnabled: () => {
        if (getFeatureFlag("integrationsv2", isBoolean, false)) {
          const currentPath = window.location.pathname;
          return !currentPath.startsWith("/integrations/abandoned-carts/");
        }
        return false;
      },
    } satisfies RouteConfig)
);

const Reports = {
  key: getNewUniqueRouteKey("reports"),
  path: "/reporting/*",
  mfe: { name: "container", entry: "app" },
  menu: {
    position: "bottom",
    icon: "mm-report",
    textKey: "menuItemReports",
    href: "/integrations/reporting/automation",
    subRouteSections: [
      {
        textKey: "menuItemReports",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("automation-reports"),
            path: "/integrations/reporting/automation/*",
            textKey: "menuItemReportsAutomationReports",
            href: "/integrations/reporting/automation",
            allowedPermissions: [REPORTING_ABANDONED_CART],
            isEnabled: ({
              integrations = [],
              integrationConnectionStates = [],
            }) =>
              hasAbandonedCartIntegration(
                integrations,
                integrationConnectionStates
              ),
          },
          {
            key: getNewUniqueRouteKey("detailed-reports"),
            path: "/reporting/detailed-reports/*",
            textKey: "menuItemReportsDetailedReports",
            mfe: { name: "MessageMediaReports", entry: "" },
            href: "/reporting/detailed-reports",
            allowedPermissions: [
              REPORTING_REPORT_DETAILED_ALL,
              REPORTING_REPORT_DETAILED_MINE,
              REPORTING_REPORT_INSIGHT_ALL,
              REPORTING_REPORT_INSIGHT_MINE,
            ],
            isEnabled: () =>
              getFeatureFlag("detailedReportsMfe", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("detailed-reports"),
            path: "/reporting/detailed-reports/*",
            textKey: "menuItemReportsDetailedReports",
            href: "/reporting/detailed-reports",
            allowedPermissions: [
              REPORTING_REPORT_DETAILED_ALL,
              REPORTING_REPORT_DETAILED_MINE,
              REPORTING_REPORT_INSIGHT_ALL,
              REPORTING_REPORT_INSIGHT_MINE,
            ],
            isEnabled: () =>
              !getFeatureFlag("detailedReportsMfe", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("customer-reports"),
            path: "/reporting/my-customers/*",
            textKey: "menuItemReportsMyCustomers",
            href: "/reporting/my-customers",
            allowedPermissions: [REPORTING_REPORT_PARTNER_CUSTOMERS],
          },
          {
            key: getNewUniqueRouteKey("delivery-status"),
            mfe: { name: "MessageMediaReports", entry: "" },
            path: "/reporting/delivery-status/*",
            textKey: "menuItemReportsDeliveryStatusReports",
            href: "/reporting/delivery-status",
            allowedPermissions: [REPORTING_REPORT_DELIVERY_STATUS],
            isEnabled: () =>
              getFeatureFlag("deliveryStatusMfeReport", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("delivery-status"),
            path: "/reporting/delivery-status/*",
            textKey: "menuItemReportsDeliveryStatusReports",
            href: "/reporting/delivery-status",
            allowedPermissions: [REPORTING_REPORT_DELIVERY_STATUS],
            isEnabled: () =>
              !getFeatureFlag("deliveryStatusMfeReport", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("scheduled-reports"),
            mfe: { name: "MessageMediaReports", entry: "" },
            path: "/reporting/scheduled-reports/*",
            textKey: "menuItemReportsScheduledReports",
            href: "/reporting/scheduled-reports",
            allowedPermissions: [REPORTING_REPORT_SCHEDULED],
            isEnabled: () =>
              getFeatureFlag("scheduledReportsMfeReport", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("scheduled-reports"),
            path: "/reporting/scheduled-reports/*",
            textKey: "menuItemReportsScheduledReports",
            href: "/reporting/scheduled-reports",
            allowedPermissions: [REPORTING_REPORT_SCHEDULED],
            isEnabled: () =>
              !getFeatureFlag("scheduledReportsMfeReport", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("sub-accounts-report"),
            path: "/reporting/sub-account-usage/*",
            textKey: "menuItemReportsSubAccountUsage",
            href: "/reporting/sub-account-usage",
            allowedPermissions: [REPORTING_REPORT_SUB_ACCOUNTS],
            isEnabled: () =>
              !getFeatureFlag("subAccountUsageMfeReport", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("sub-accounts-report"),
            mfe: { name: "MessageMediaReports", entry: "" },
            path: "/reporting/sub-account-usage/*",
            textKey: "menuItemReportsSubAccountUsage",
            href: "/reporting/sub-account-usage",
            allowedPermissions: [REPORTING_REPORT_SUB_ACCOUNTS],
            isEnabled: () =>
              getFeatureFlag("subAccountUsageMfeReport", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("user-usage-report"),
            path: "/reporting/user-usage-report/*",
            textKey: "menuItemReportsUserUsageReport",
            href: "/reporting/user-usage-report",
            allowedPermissions: [REPORTING_REPORT_USERS],
            mfe: { name: "MessageMediaReports", entry: "" },
            isEnabled: () =>
              getFeatureFlag("userUsageReportMfeReport", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("user-usage-report"),
            path: "/reporting/user-usage-report/*",
            textKey: "menuItemReportsUserUsageReport",
            href: "/reporting/user-usage-report",
            allowedPermissions: [REPORTING_REPORT_USERS],
            isEnabled: () =>
              !getFeatureFlag("userUsageReportMfeReport", isBoolean, false),
          },
        ],
      },
    ],
  },
} satisfies RouteConfigMenu;

const MfeSettings = {
  key: getNewUniqueRouteKey("configuration"),
  path: "/account-settings/*",
  mfe: {
    name: "MfeAccount",
    entry: "app",
  },
  isEnabled: () => true,
  allowedPermissions: [
    ACCOUNT_SETTINGS,
    ACCOUNT_SETTINGS_SSO,
    API_KEYS_BASIC,
    API_KEYS_HMAC,
    API_KEYS_LEGACY,
    BILLING_DETAILS,
    REPORTING_ACTIVITY_LOG,
    PARTNERS_LIST_CUSTOMER_ACCOUNT,
    MANAGEMENT_SUB_ACCOUNTS,
    MANAGEMENT_USERS,
  ],
  menu: {
    position: "bottom",
    icon: "mm-settings",
    textKey: "menuItemMMSettings",
    href: "/account-settings",
    subRouteSections: [
      {
        textKey: "menuItemMMSettings",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("account-settings"),
            path: "/account-settings/*",
            textKey: "menuItemMMSettingsAccount",
            href: "/account-settings",
            allowedPermissions: [
              ACCOUNT_SETTINGS,
              ACCOUNT_SETTINGS_SSO,
              REPORTING_ACTIVITY_LOG,
            ],
          },
          {
            key: getNewUniqueRouteKey("numbers-and-channels"),
            path: "/numbers/*",
            isEnabled: ({ userCountry }) =>
              !getVendorChecks().isNZCarriers &&
              !(getVendorChecks().isBrandMM && userCountry == "NZ"),
            mfe: { name: "container", entry: "app" },
            textKey: "menuItemMMSettingsNumbersChannels",
            href: "/numbers",
          },
          {
            key: getNewUniqueRouteKey("messaging-settings"),
            path: "/messaging-settings/*",
            isEnabled: ({ userCountry }) =>
              getVendorChecks()?.isNZCarriers ||
              (getVendorChecks()?.isBrandMM && userCountry == "NZ") ||
              false,
            mfe: { name: "container", entry: "app" },
            textKey: "menuItemMMSettingsMessagingSettings",
            href: "/messaging-settings",
          },
          {
            key: getNewUniqueRouteKey("plan-selection"),
            path: "/plan-selection/*",
            mfe: { name: "container", entry: "app" },
            textKey: "menuItemMMSettingsPlanSelection",
            href: "/plan-selection",
            allowedPermissions: [PRODUCTS_SELECTION],
          },
          {
            key: getNewUniqueRouteKey("social-channels"),
            path: "/social-channels/*",
            textKey: "menuItemMMSettingsSocialChannels",
            mfe: { name: "Channels", entry: "" },
            isEnabled: () =>
              getVendorChecks().isBrandAppSinch ||
              getVendorChecks().isBrandSE ||
              getVendorChecks().isBrandMM,
            href: "/social-channels",
          },
          {
            key: getNewUniqueRouteKey("web-widgets"),
            path: "/web-widgets/*",
            textKey: "menuItemWebWidgets",
            mfe: { name: "WebWidgets", entry: "" },
            isEnabled: () =>
              getFeatureFlag(
                "conversationStarters_feature_widget",
                isBoolean,
                false
              ),
            href: "/web-widgets",
          },
          {
            key: getNewUniqueRouteKey("billing"),
            path: "/billing/*",
            textKey: "menuItemMMSettingsBilling",
            mfe: { name: "MfeBilling", entry: "" },
            href: "/billing",
            allowedPermissions: [BILLING_DETAILS],
            isEnabled: () => true,
          },
          {
            key: getNewUniqueRouteKey("customer-accounts"),
            path: "/account-settings?tab=customer-accounts",
            textKey: "menuItemMMSettingsCustomerAccounts",
            href: "/account-settings?tab=customer-accounts",
            mfe: { name: "container", entry: "app" },
            allowedPermissions: [PARTNERS_LIST_CUSTOMER_ACCOUNT],
          },
          {
            key: getNewUniqueRouteKey("sub-accounts"),
            path: "/account-settings?tab=sub-accounts",
            textKey: "menuItemMMSettingsSubAccounts",
            mfe: { name: "container", entry: "app" },
            href: "/account-settings?tab=sub-accounts",
            allowedPermissions: [MANAGEMENT_SUB_ACCOUNTS],
          },
          {
            key: getNewUniqueRouteKey("users"),
            path: "/users/*",
            textKey: "menuItemMMSettingsUsers",
            mfe: { name: "container", entry: "app" },
            href: "/users",
            allowedPermissions: [MANAGEMENT_USERS],
          },
          {
            key: getNewUniqueRouteKey("activity-log"),
            path: "/activity-log",
            textKey: "menuItemMMSettingsActivityLog",
            mfe: { name: "container", entry: "app" },
            href: "/activity-log",
            allowedPermissions: [REPORTING_ACTIVITY_LOG],
          },
          {
            key: getNewUniqueRouteKey("api-settings"),
            path: "/api-settings/*",
            textKey: "menuItemMMSettingsApiSettings",
            mfe: { name: "container", entry: "app" },
            href: "/api-settings",
            allowedPermissions: [
              API_KEYS_BASIC,
              API_KEYS_HMAC,
              API_KEYS_LEGACY,
            ],
          },
          {
            key: getNewUniqueRouteKey("chatbox-sms"),
            path: "/extensions/sms-chat-widget/*",
            textKey: "menuItemMMSettingsChatboxSms",
            mfe: { name: "container", entry: "app" },
            href: "/extensions/sms-chat-widget",
            isEnabled: () => getFeatureFlag("extensionsV2", isBoolean, false),
            allowedPermissions: [WEB_WIDGET],
          },
          {
            key: getNewUniqueRouteKey("attribution"),
            path: "/integrations/attribute-setting/*",
            textKey: "menuItemMMSettingsAttribution",
            mfe: { name: "container", entry: "app" },
            href: "/integrations/attribute-setting",
            isEnabled: () =>
              hasIntegration(getAllIntegrations(), getAllConnectionStates()),
          },
        ],
      },
    ],
  },
} satisfies RouteConfigMenu;

const AgenticConversation = {
  key: getNewUniqueRouteKey("ai-agents"),
  path: "/ai-agents/*",
  isEnabled: () =>
    getFeatureFlag("enableAgenticconversationsmfe", isBoolean, false),
  mfe: { name: "AgenticConversationsMFE", entry: "" },
  menu: {
    position: "top",
    icon: "robot",
    textKey: "menuItemAgenticConversation",
    href: "/ai-agents",
    subRouteSections: [
      {
        textKey: "menuItemAgenticConversation",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("ai-agents-home"),
            path: "/ai-agents/*",
            href: "/ai-agents/",
          },
          {
            key: getNewUniqueRouteKey("ai-agents-dashboard"),
            path: "/ai-agents/dashboard/*",
            textKey: "menuItemAgenticConversationDashboard",
            href: "/ai-agents/dashboard",
            isEnabled: () =>
              getFeatureFlag(
                "enableAgenticConversationsDashboard",
                isBoolean,
                false
              ),
          },
          {
            key: getNewUniqueRouteKey("ai-agents-agents"),
            path: "/ai-agents/agents/*",
            textKey: "menuItemAgenticConversationAgents",
            href: "/ai-agents/agents",
          },
          {
            key: getNewUniqueRouteKey("ai-agents-settings"),
            path: "/ai-agents/settings/*",
            textKey: "menuItemAgenticConversationSettings",
            href: "/ai-agents/settings",
          },
        ],
      },
    ],
  },
} satisfies RouteConfigMenu;

export const loginHubPaths = [
  "/accept-invitation",
  "/bigcommerce/*",
  "/shopify/*",
  "/connect/*",
  "/dev-signup/*",
  "/forgot-password/*",
  "/invitation/*",
  "/login/*",
  "/opt-out-v2/*",
  "/opt-out/*",
  "/payment-success/*",
  "/pin-verification/*",
  "/profiling-questionnaire-role-and-company",
  "/profiling-questionnaire-intend",
  "/profiling-questionnaire-intend-use-case",
  "/profiling-questionnaire-channels",
  "/profiling-questionnaire-multiple-channels",
  "/profiling-questionnaire-multiple-channels",
  "/profiling-questionnaire-multiple-channels-other-tools",
  "/profiling-questionnaire-goals",
  "/register-new/*",
  "/register/*",
  "/business-registration/*",
  "/registration/*",
  "/reset-password/*",
  "/set-password/*",
  "/shopify/*",
  "/sign-up/*",
  "/signup/*",
  "/token-handler/*",
  "/sso-handler/*",
  "/verify/*",
  "/new-mm-registration/*",
  "/user-invitation",
];

/**
 * Paths loading the MFE Login
 */
export const mfeLoginPaths = [
  "/token-handler/*",
  "/sso-handler/*",
  "/login/*",
  "/forgot-password/*",
  "/pin-verification/*",
];
/**
 * Authenticated routes of MFE Login
 */
const authenticatedMfeLoginPaths = ["/logout/*"];

/**
 * Paths loading the HUB Container without the sideMenu
 */
export const nonMenuHubContainerPaths = [
  ...loginHubPaths,
  "/support/*", //Support pages appear in the authed state of the app, but currently are not integrated in the new menu
  "/integrations/workato",
];

/**
 * Paths that should render the hub even if they're not part of the menu
 */
const extraHubPaths = [
  ...nonMenuHubContainerPaths,
  "/profile/*",
].map<RouteConfig>((path) => ({
  key: getNewUniqueRouteKey(path),
  path,
  mfe: { name: "container", entry: "app" },
})) satisfies RouteConfig[];

const authenticatedMfeLoginRoutes = authenticatedMfeLoginPaths.map<RouteConfig>(
  (path) => ({
    key: getNewUniqueRouteKey(path),
    path,
    mfe: { name: "MfeLogin", entry: "app" },
  })
) satisfies RouteConfig[];
export const mfeLoginRoutes = [
  ...mfeLoginPaths,
  ...authenticatedMfeLoginPaths,
].map<RouteConfig>((path) => ({
  key: getNewUniqueRouteKey(path),
  path,
  mfe: { name: "MfeLogin", entry: "app" },
})) satisfies RouteConfig[];

const SenderRegistrationRoute = {
  key: getNewUniqueRouteKey("sender-registration"),
  path: "/numbers/sender-registration/*",
  mfe: { name: "SenderRegistration", entry: "" },
} satisfies RouteConfig;

export const nonMenuRoutes = [
  ...extraHubPaths,
  ...authenticatedMfeLoginRoutes,
  MessageMediaUpgrade,
  ...MessageMediaPublicFacingRoutes,
  ...MfeEcommerce,
  ...MfeIntegrations,
  SenderRegistrationRoute,
];

export const mmNavigationConfig = [
  MfeDashboard,
  Inbox,
  InboxV2ToV4Redirect,
  HTMXInbox,
  AgenticConversation,
  Campaigns,
  messageReportUUID,
  Automations,
  Templates,
  MobileLandingPages,
  ContactsV2,
  Extensions,
  Reports,
  MfeSettings,
  MessageMediaUpgrade,
  automationIdContainer,
  automationIdMfe,
  automationTemplatesContainer,
];
